import React, { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { Notification, TNotificationStatus } from './Notification/Notification';
import { CSSTransition } from 'react-transition-group';
import styles from './Notification/Notification.module.scss';
import classNames from 'classnames/bind';

const cx = classNames.bind(styles);

export interface INotification {
  message: ReactNode;
  severity: TNotificationStatus;
  description?: string;
}

export type TCreateNotification = (notification: INotification) => void;
// eslint-disable-next-line @typescript-eslint/no-empty-function
const NotificationCtx = createContext<TCreateNotification>(() => {});
export const useNotifications = (): TCreateNotification => useContext(NotificationCtx);
const animationDuration = 300;
export const NotificationContext = ({ children, offset = false }: { children: ReactNode; offset?: boolean }) => {
  const [notification, setNotification] = useState<INotification>();
  const [isNotificationVisible, setIsNotificationVisible] = useState<boolean>(false);
  const ref = useRef(null);
  const createNotification = (newNotification: INotification) => {
    setIsNotificationVisible(false);
    if (notification) {
      setTimeout(() => {
        setNotification(newNotification);
      }, animationDuration);
    } else {
      setNotification(newNotification);
    }
  };

  useEffect(() => {
    if (notification) {
      setIsNotificationVisible(true);
    }
  }, [notification]);

  return (
    <NotificationCtx.Provider value={createNotification}>
      {children}
      <CSSTransition
        nodeRef={ref}
        in={isNotificationVisible}
        timeout={animationDuration}
        unmountOnExit
        classNames={{
          enter: cx('animation-enter'),
          enterActive: cx('animation-enter-active'),
          exit: cx('animation-exit'),
          exitActive: cx('animation-exit-active'),
        }}
      >
        <div ref={ref} className={cx('animation', { offset: offset })}>
          <Notification
            title={notification?.message}
            description={notification?.description}
            status={notification ? notification.severity : 'success'}
            onClose={() => setIsNotificationVisible(false)}
          />
        </div>
      </CSSTransition>
    </NotificationCtx.Provider>
  );
};
